<template>
	<div class="search-results main-container">
		<h1>{{ categoryTitle }}</h1>
		<div v-if="loading && results.length === 0" class="loading-state">
			<pulse-loader :loading="true" :color="'white'" :size="'12px'"></pulse-loader>
		</div>
		<div v-else-if="results.length === 0">{{ $t("search.no_results") }}.</div>

		<div class="results" v-focus-section v-else>
			<div class="item" v-focus v-for="(item, index) in results" :key="index">
				<component :is="getTeaserComponent(item)" :item="item" />
			</div>
		</div>
	</div>
	<TeaserPreview />
</template>

<script lang="ts" setup>
	import { ref, watch, computed, onMounted } from "vue"
	import { useRoute } from "vue-router"
	import { TeaserSmall } from "../components/teasers"
	import { useTaxonomyStore } from "../stores/taxonomies"
	import { useTeaserStore } from "../stores/teaser"
	import TeaserPreview from "../components/video/TeaserPreview.vue"
	import api from "../composables/useApi"
	import { useI18n } from "vue-i18n"

	const teaserStore = useTeaserStore()
	const route = useRoute()
	const results = ref<any[]>([])
	const loading = ref(false)
	const taxonomyStore = useTaxonomyStore()
	const { locale } = useI18n()

	const getTeaserComponent = (item: any) => {
		return TeaserSmall
	}

	// Get the taxonomy ID from route params
	const categoryId = computed(() => Number(route.params.tid))

	// Get category title from taxonomy store
	const categoryTitle = computed(() => {
		return taxonomyStore.getCategoryTitle(categoryId.value) || "Category"
	})

	// Determine the content type based on route name
	const contentType = computed(() => {
		if (route.name === "CategoryMovies") return "movie"
		if (route.name === "CategorySeries") return "series"
		return "both" // CategoryPage (mixed)
	})

	// Fetch category data from JSON:API
	const fetchCategoryData = async (tid: number) => {
		loading.value = true
		results.value = []

		try {
			const requests = []

			// Fetch movies if needed
			if (contentType.value === "movie" || contentType.value === "both") {
				requests.push(
					api.get(`${locale.value}/jsonapi/node/movie?jsonapi_include=1&include=field_trailer_file,field_image`, {
						params: {
							"filter[field_category.meta.drupal_internal__target_id]": tid,
							"filter[langcode]": locale.value,
							"filter[status]": 1,
							"page[limit]": 50,
							sort: "-changed",
						},
					})
				)
			}

			// Fetch series if needed
			if (contentType.value === "series" || contentType.value === "both") {
				requests.push(
					api.get(`${locale.value}/jsonapi/node/series?jsonapi_include=1&include=field_image`, {
						params: {
							"filter[field_category.meta.drupal_internal__target_id]": tid,
							"filter[langcode]": locale.value,
							"filter[status]": 1,
							"page[limit]": 50,
						},
					})
				)
			}

			const responses = await Promise.all(requests)

			// Combine results from both requests - data is already processed by jsonapi_include
			results.value = responses.flatMap((response) => response.data.data || [])
		} catch (error) {
			console.error("Failed to fetch category data:", error)
			results.value = []
		} finally {
			loading.value = false
		}
	}

	// Watch for category ID changes
	watch(
		() => categoryId.value,
		async (newTid) => {
			if (newTid) {
				await fetchCategoryData(newTid)
			}
		},
		{ immediate: true }
	)

	onMounted(() => {
		teaserStore.setSelectedItem(undefined)
	})
</script>

<style lang="scss" scoped>
	h1 {
		margin-bottom: 35px;

		@include br.respond-to(br.$tablet-large) {
			font-size: 26px;
			line-height: 38px;
			margin-bottom: 25px;
		}
	}
	.search-results {
		padding: 80px 0;

		@include br.tablet-down {
			padding: 120px 0;
		}
	}
	.results {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
		column-gap: 20px;
		row-gap: 45px;

		@include br.tablet-down {
			grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
			column-gap: 15px;
			row-gap: 20px;
		}
	}
	.loading-state {
		text-align: center;
		padding: 40px 0;
	}
</style>
